// The places netti hands you in a conversation, and the replies it offers next.
// ONE contract for every surface that draws a netti answer: the dashboard's
// ask-netti rail (NettiChat), the Mac app's desk rail (Rail.tsx), the stored
// conversation (netti_conversations.messages, jsonb, so new fields cost no
// schema change), the ask frame (agent.ts NettiFrame) and the hands wire
// (hands/wire.ts HandsResponse). Every field past label + path is optional:
// conversations stored before this file carry {label, path} only and must
// still draw.
//
// A link is drawn as a ROW in the dashboard's list language (law_list_language,
// revised 2026-10-15 for chat): the folder icon holding its emoji, or the page
// or artifact glyph, then the name with the living-colour underline, then where
// it lives as quiet words. A setting reads as a row whose trail is clickable
// ("settings › data"); a connection carries its state word.

export type ChatLinkKind =
  | 'folder'      // a project or a group of pages: wears its emoji
  | 'page'        // a dimension / page of a map
  | 'artifact'    // a document netti or a tool made
  | 'skill'       // a skill, in the map or the commons
  | 'setting'     // a place in settings (a panel and a tab)
  | 'connection'  // a tool connected to innernet
  | 'fact'        // a personal fact on the you page
  | 'activity'    // a moment in the activity river
  | 'task'        // a task
  | 'agent'       // one of the person's agents
  | 'docs'        // a page of the docs
  | 'place'       // a place in the shell (you, netti, connections…)
  | 'web'         // a page on the open web (opens in the browser)

export type ChatLinkState = 'live' | 'active' | 'quiet' | 'waiting'

export interface ChatLinkStep {
  label: string
  path: string
}

export interface ChatLink {
  /** the name, as the person would say it */
  label: string
  /** an in-shell path (`/projects/<id>/…`, `/settings`, `/you#fact-…`) or, for kind web, an https url */
  path: string
  kind?: ChatLinkKind
  /** a folder's emoji, or a page's glyph (lib/os/symbols) */
  symbol?: string
  /** a folder's own colour when the person picked one (#rrggbb) */
  color?: string
  /** where it lives, in words: "in innernet", "a skill in the commons" */
  meta?: string
  /** for a setting: the way there, each step its own link ("settings › data") */
  trail?: ChatLinkStep[]
  /** for a connection: its state word */
  state?: ChatLinkState
}

/** the replies netti offers above the composer: one to three, a few words each */
export const MAX_REPLIES = 3
export const MAX_REPLY_CHARS = 40
export const MAX_LINKS = 12

const KINDS: ReadonlySet<string> = new Set<ChatLinkKind>([
  'folder', 'page', 'artifact', 'skill', 'setting', 'connection', 'fact',
  'activity', 'task', 'agent', 'docs', 'place', 'web',
])
const STATES: ReadonlySet<string> = new Set<ChatLinkState>(['live', 'active', 'quiet', 'waiting'])

/** the shell's own places: a path that starts with one of these is a place, not a page */
const SHELL_PLACES = new Set([
  'you', 'memory', 'netti', 'skills', 'connections', 'agents', 'activity', 'tasks', 'parked', 'settings', 'ours', 'projects',
])

const SAFE_IN_SHELL = /^\/(?![/\\])[^\s<>"'`]*$/
const SAFE_WEB = /^https:\/\/[^\s<>"'`]+$/i
const HEX = /^#[0-9a-f]{6}$/i

/** is this a path the shell (or, for web, the browser) may be sent to? */
export function safeLinkPath(path: unknown, kind?: ChatLinkKind): path is string {
  if (typeof path !== 'string' || path.length === 0 || path.length > 600) return false
  if (kind === 'web') return SAFE_WEB.test(path)
  return SAFE_IN_SHELL.test(path) || SAFE_WEB.test(path)
}

/**
 * The kind of a place, read from its path and (when a tool line carried it) the
 * bracket the model saw: `[space]`, `[dimension in X]`, `[artifact in X]`,
 * `[node in X]`, `[skill in X]`, `[activity in X]`, `[docs]`.
 */
export function linkKindOf(path: string, bracket?: string | null): ChatLinkKind {
  const b = (bracket ?? '').trim().toLowerCase()
  if (b) {
    if (b.startsWith('space') || b.startsWith('project') || b.startsWith('folder') || b.startsWith('group')) return 'folder'
    if (b.startsWith('artifact')) return 'artifact'
    if (b.startsWith('skill')) return 'skill'
    if (b.startsWith('activity')) return 'activity'
    if (b.startsWith('docs')) return 'docs'
    if (b.startsWith('task')) return 'task'
    if (b.startsWith('fact')) return 'fact'
    if (b.startsWith('setting')) return 'setting'
    if (b.startsWith('connection') || b.startsWith('tool')) return 'connection'
    if (b.startsWith('dimension') || b.startsWith('node') || b.startsWith('page') || b.startsWith('cell')) return 'page'
  }
  if (SAFE_WEB.test(path)) return 'web'
  const clean = path.split(/[?#]/)[0]
  const seg = clean.split('/').filter(Boolean)
  if (seg.length === 0) return 'place'
  const [head] = seg
  if (head === 'projects') {
    if (seg.length <= 2) return 'folder'
    if (seg[2] === 'artifacts') return 'artifact'
    if (seg[2] === 'skills') return seg.length === 3 ? 'folder' : 'skill'
    return 'page'
  }
  if (head === 'artifacts') return 'artifact'
  if (head === 'skills') return seg.length === 1 ? 'place' : 'skill'
  if (head === 'docs') return 'docs'
  if (head === 'settings') return 'setting'
  if (head === 'connections') return seg.length === 1 ? 'place' : 'connection'
  if (head === 'agents') return seg.length === 1 ? 'place' : 'agent'
  if (head === 'tasks') return seg.length === 1 ? 'place' : 'task'
  if (head === 'activity') return path.includes('#') ? 'activity' : 'place'
  if (head === 'you') return path.includes('#fact-') ? 'fact' : 'place'
  if (SHELL_PLACES.has(head)) return 'place'
  return 'page'
}

function str(v: unknown, max: number): string | undefined {
  if (typeof v !== 'string') return undefined
  const t = v.replace(/\s+/g, ' ').trim()
  if (!t) return undefined
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t
}

/** one link, made safe to draw (from a tool, the model's frame, or stored json); null when unusable */
export function cleanLink(raw: unknown): ChatLink | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const label = str(r.label, 80)
  const kindIn = typeof r.kind === 'string' && KINDS.has(r.kind) ? (r.kind as ChatLinkKind) : undefined
  if (!label || !safeLinkPath(r.path, kindIn)) return null
  const path = r.path as string
  const out: ChatLink = { label, path, kind: kindIn ?? linkKindOf(path) }
  const symbol = str(r.symbol, 8)
  if (symbol) out.symbol = symbol
  if (typeof r.color === 'string' && HEX.test(r.color)) out.color = r.color.toLowerCase()
  const meta = str(r.meta, 60)
  if (meta) out.meta = meta
  if (typeof r.state === 'string' && STATES.has(r.state)) out.state = r.state as ChatLinkState
  if (Array.isArray(r.trail)) {
    const trail = r.trail
      .map((s) => {
        if (!s || typeof s !== 'object') return null
        const t = s as Record<string, unknown>
        const l = str(t.label, 32)
        return l && safeLinkPath(t.path) ? { label: l, path: t.path as string } : null
      })
      .filter((s): s is ChatLinkStep => s !== null)
      .slice(0, 4)
    if (trail.length) out.trail = trail
  }
  return out
}

/** a list of links, cleaned, de-duplicated by path, capped */
export function cleanLinks(raw: unknown, max = MAX_LINKS): ChatLink[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  const out: ChatLink[] = []
  for (const r of raw) {
    const l = cleanLink(r)
    if (!l || seen.has(l.path)) continue
    seen.add(l.path)
    out.push(l)
    if (out.length >= max) break
  }
  return out
}

/** the offered replies, made safe: one to three short lines, no duplicates, no markup */
export function cleanReplies(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const r of raw) {
    if (typeof r !== 'string') continue
    const t = r.replace(/[\r\n\t]+/g, ' ').replace(/[`*_#>[\]]/g, '').replace(/\s+/g, ' ').trim().replace(/[.!]+$/, '')
    if (!t || t.length > MAX_REPLY_CHARS) continue
    const key = t.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(t)
    if (out.length >= MAX_REPLIES) break
  }
  return out
}
